import React from 'react';
import { CurrentUser, DataRoom, IntegrationConfig } from '../models';
import { useAuth } from './AuthContext';
import { useLocation, useParams } from 'react-router-dom';
import { useDataRoomQuery, useIntegrationConfigQuery } from '../hooks';

type CerebroNavContext = {
	navItems: NavItem[];
	currentNavItem?: NavLink;
	nextNavItem?: NavLink;
	setState: (state: ReturnType<typeof buildNavItems>) => void;
};

const Context = React.createContext<CerebroNavContext | null>(null);

export const CerebroNavProvider: React.FC = ({ children }) => {
	const { currentUser, isTeamMember } = useAuth();
	const { pathname } = useLocation();

	const [navItems, setNavItems] = React.useState<NavItem[]>([]);
	const [currentNavItem, setCurrentNavItem] = React.useState<NavLink>();
	const [nextNavItem, setNextNavItem] = React.useState<NavLink>();
	const { guid } = useParams<'guid'>();

	const { data: dataRoom } = useDataRoomQuery(guid);
	const { data: integrations } = useIntegrationConfigQuery();

	const setState = ({ navItems, currentNavItem, nextNavItem }: ReturnType<typeof buildNavItems>) => {
		setNavItems(navItems);
		setCurrentNavItem(currentNavItem);
		setNextNavItem(nextNavItem);
	};

	React.useEffect(() => {
		setState(buildNavItems(currentUser, isTeamMember, dataRoom, pathname, integrations));
	}, [currentUser, dataRoom, pathname]); /* eslint-disable-line react-hooks/exhaustive-deps */

	React.useEffect(() => {
		window.scrollTo(0, 0);
	}, [pathname]);

	const context: CerebroNavContext = {
		navItems,
		currentNavItem,
		nextNavItem,
		setState,
	};

	return <Context.Provider value={context}>{children}</Context.Provider>;
};

export function useCerebroNav() {
	var context = React.useContext(Context);

	if (context == null) throw Error('You tried to consume the CerebroNavContext outside of a CerebroNavProvider!');

	return context;
}

export const Urls = {
	Summary: { Root: 'Summary' },
	Matching: { Root: 'Matching' },
	Settings: { Root: 'Settings' },
	LoanApplication: { Root: 'LoanApplication' },
	ActivityLog: { Root: 'ActivityLog' },
	Documents: { Root: 'Documents' },
	DocumentFolder: { Root: 'Documents/:folderGuid' },
	Teasers: { Root: 'Teasers' },
	LenderNda: { Root: 'LenderNda' },
	Nda: { Root: 'Nda' },
	Participants: {
		Root: 'Participants',
		Borrowers: 'Participants/Borrowers',
		ThirdParty: 'Participants/ThirdParty',
		Partners: 'Participants/Partners',
	},
	Milestones: {
		Root: 'Milestones',
		LenderSummary: 'Milestones/LenderSummaryReport',
		ActiveLenderSummary: 'Milestones/ActiveLenderSummaryReport',
	},
	Calls: { Root: 'Calls' },
	TeamMembers: {
		Root: 'TeamMembers',
	},
	DiligenceRequests: {
		Root: 'DiligenceRequests',
	},
	TermSheets: {
		Root: 'TermSheets',
		ComparisonReport: 'TermSheets/ComparisonReport',
		RevisionForm: 'TermSheets/:termSheetGuid/Revisions/:revisionGuid',
	},
	RelatedDeals: { Root: 'RelatedDeals' },
	LenderInvites: { Root: 'LenderInvites' },
};

export function buildNavItems(
	currentUser: CurrentUser,
	isTeamMember: boolean,
	dataRoom: DataRoom | undefined,
	currentPath: string,
	integrations: IntegrationConfig | undefined
) {
	const isEmployeeUser = currentUser?.isEmployeeUser ?? false;
	const isNotBankUser = !(currentUser?.isBankUser ?? false);
	const isBankUser = currentUser?.isBankUser ?? false;
	const isBankAndNotTeamMemberUser = isBankUser && !isTeamMember;
	const isNotCompanyUser = !(currentUser.isCompanyUser ?? false);
	const feedbackSummaryVar = dataRoom?.showFeedbackSummary ?? false;
	const isRawLead = dataRoom?.isRawLead ?? false;
	const activeFeedbackSummaryVar = dataRoom?.showActiveSummaryReport ?? false;
	const isDirectIntroDeal = dataRoom?.isDirectIntroDeal ?? false;
	const showLenderSummary = !isDirectIntroDeal && (isEmployeeUser || feedbackSummaryVar);
	const showActiveLenderSummary = !isDirectIntroDeal && (isEmployeeUser || activeFeedbackSummaryVar);

	const connectNavItems: NavItem[] = [
		{
			text: 'Documents',

			url: Urls.Documents.Root,
			urlRootForSection: Urls.Documents.Root,

			absolute: false,
			disabled: false,
			completed: false,

			children: [],
		},
		{
			text: 'Loan Application',
			url: Urls.LoanApplication.Root,
			urlRootForSection: Urls.LoanApplication.Root,
			absolute: false,
			disabled: false,
			completed: false,

			children: [],
		},
		{
			text: 'Lender Invites',
			url: Urls.LenderInvites.Root,
			urlRootForSection: Urls.LenderInvites.Root,
			absolute: false,
			disabled: false,
			completed: false,

			children: [],
		},
		{ key: '01_Separator', isSeparator: true },
		{
			text: 'Matching',
			url: Urls.Matching.Root,
			urlRootForSection: Urls.Matching.Root,
			absolute: false,
			completed: false,
			disabled: false,
			children: [],
		},
		...(dataRoom?.hubSpotId && integrations && integrations.hubspotPortalId
			? ([
					{
						text: 'HubSpot',

						url: `https://app.hubspot.com/contacts/${integrations.hubspotPortalId}/deal/${dataRoom.hubSpotId}/`,
						urlRootForSection: `https://app.hubspot.com/contacts/${integrations.hubspotPortalId}/deal/${dataRoom.hubSpotId}/`,

						absolute: true,
						completed: false,
						disabled: false,

						children: [],
					},
			  ] as NavItem[])
			: []),
	];

	const mainNavItems: NavItem[] = [
		{
			text: 'Documents',

			url: Urls.Documents.Root,
			urlRootForSection: Urls.Documents.Root,

			absolute: false,
			disabled: false,
			completed: false,

			children: [],
		},
		{
			text: 'Loan Application',

			url: Urls.LoanApplication.Root,
			urlRootForSection: Urls.LoanApplication.Root,
			absolute: false,
			disabled: false,
			completed: false,

			children: [],
		},
		...(isNotBankUser
			? [
					{
						text: 'Data Room Summary',

						url: Urls.Summary.Root,
						urlRootForSection: Urls.Summary.Root,

						absolute: false,
						disabled: false,
						completed: false,

						children: [],
					},
					{
						text: 'Participants',

						url: Urls.Participants.Root,
						urlRootForSection: Urls.Participants.Root,

						absolute: false,
						disabled: false,
						completed: false,

						children: [
							{
								text: 'Lenders',

								url: Urls.Participants.Root,
								urlRootForSection: Urls.Participants.Root,

								absolute: false,
								disabled: false,
								completed: false,

								children: [],
							},
							{
								text: 'Borrowers',

								url: Urls.Participants.Borrowers,
								urlRootForSection: Urls.Participants.Root,

								absolute: false,
								disabled: false,
								completed: false,

								children: [],
							},

							...(dataRoom?.dealPartnerContact != null
								? [
										{
											text: 'Deal Partners',

											url: Urls.Participants.Partners,
											urlRootForSection: Urls.Participants.Root,

											absolute: false,
											disabled: false,
											completed: false,

											children: [],
										},
								  ]
								: []),
						],
					},
					{
						text: 'Lender NDA',

						url: Urls.LenderNda.Root,
						urlRootForSection: Urls.LenderNda.Root,

						absolute: false,
						disabled: false,
						completed: false,

						children: [],
					},
					{
						text: 'Teasers',

						url: Urls.Teasers.Root,
						urlRootForSection: Urls.Teasers.Root,

						absolute: false,
						disabled: false,
						completed: false,

						children: [],
					},
					{
						text: 'Lender Progress',

						url: Urls.Milestones.Root,
						urlRootForSection: Urls.Milestones.Root,

						absolute: false,
						disabled: false,
						completed: false,

						children:
							showLenderSummary && showActiveLenderSummary
								? [
										{
											text: 'Feedback Summary Report',

											url: Urls.Milestones.LenderSummary,
											urlRootForSection: Urls.Milestones.Root,

											absolute: false,
											disabled: false,
											completed: false,

											children: [],
										},
										{
											text: 'Active Summary Report',

											url: Urls.Milestones.ActiveLenderSummary,
											urlRootForSection: Urls.Milestones.Root,

											absolute: false,
											disabled: false,
											completed: false,

											children: [],
										},
								  ]
								: showLenderSummary
								? [
										{
											text: 'Feedback Summary Report',

											url: Urls.Milestones.LenderSummary,
											urlRootForSection: Urls.Milestones.Root,

											absolute: false,
											disabled: false,
											completed: false,

											children: [],
										},
								  ]
								: showActiveLenderSummary
								? [
										{
											text: 'Active Summary Report',

											url: Urls.Milestones.ActiveLenderSummary,
											urlRootForSection: Urls.Milestones.Root,

											absolute: false,
											disabled: false,
											completed: false,

											children: [],
										},
								  ]
								: [],
					},
			  ]
			: []),
		...(isBankAndNotTeamMemberUser
			? [
					{
						text: 'Team Members',

						url: Urls.TeamMembers.Root,
						urlRootForSection: Urls.TeamMembers.Root,

						absolute: false,
						disabled: false,
						completed: false,

						children: [],
					},
			  ]
			: []),

		{
			text: 'Call Scheduling',

			url: Urls.Calls.Root,
			urlRootForSection: Urls.Calls.Root,

			absolute: false,
			disabled: false,
			completed: false,

			children: [],
		},

		{
			text: 'Diligence Requests',

			url: Urls.DiligenceRequests.Root,
			urlRootForSection: Urls.DiligenceRequests.Root,

			absolute: false,
			disabled: false,
			completed: false,

			children: [],
		},

		{
			text: 'Term Sheets',

			url: Urls.TermSheets.Root,
			urlRootForSection: Urls.TermSheets.Root,

			absolute: false,
			disabled: false,
			completed: false,

			children: [
				...(isEmployeeUser
					? [
							{
								text: 'Comparison Report',

								url: Urls.TermSheets.ComparisonReport,
								urlRootForSection: Urls.TermSheets.Root,

								absolute: false,
								disabled: false,
								completed: false,

								children: [],
							},
					  ]
					: []),
			],
		},

		{ key: '01_Separator', isSeparator: true },

		...(isEmployeeUser
			? [
					{
						text: 'Matching',
						url: Urls.Matching.Root,
						urlRootForSection: Urls.Matching.Root,
						absolute: false,
						completed: false,
						disabled: false,
						children: [],
					},
					{
						text: 'Settings',
						url: Urls.Settings.Root,
						urlRootForSection: Urls.Settings.Root,
						absolute: false,
						completed: false,
						disabled: false,
						children: [],
					},
			  ]
			: []),
		...(!isEmployeeUser && isNotCompanyUser
			? [
					{
						text: 'NDA',

						url: Urls.Nda.Root,
						urlRootForSection: Urls.Nda.Root,

						absolute: false,
						disabled: false,
						completed: false,

						children: [],
					},
			  ]
			: []),
		{
			text: 'Activity Log',
			url: Urls.ActivityLog.Root,
			urlRootForSection: Urls.ActivityLog.Root,
			absolute: false,
			completed: false,
			disabled: false,
			children: [],
		},
		...(isEmployeeUser && dataRoom?.hubSpotId && integrations && integrations.hubspotPortalId
			? ([
					{
						text: 'HubSpot',

						url: `https://app.hubspot.com/contacts/${integrations.hubspotPortalId}/deal/${dataRoom.hubSpotId}/`,
						urlRootForSection: `https://app.hubspot.com/contacts/${integrations.hubspotPortalId}/deal/${dataRoom.hubSpotId}/`,

						absolute: true,
						completed: false,
						disabled: false,

						children: [],
					},
			  ] as NavItem[])
			: []),
	];

	const navItems = !dataRoom ? [] : isRawLead && isEmployeeUser ? connectNavItems : mainNavItems;

	const flattened = navItems
		.filter(isNavLink)
		.filter(navLink => !navLink.disabled)
		.flatMap(flatMapLinks);
	const currentIndex = flattened.findIndex(navItem => currentPath.endsWith(navItem.url));
	const currentNavItem = currentIndex > -1 ? flattened[currentIndex] : undefined;
	const nextNavItem =
		currentIndex > -1 && currentIndex + 1 < flattened.length ? flattened[currentIndex + 1] : undefined;

	return { navItems, currentNavItem, nextNavItem };
}

export type NavItem = NavLink | NavSeparator;

interface NavLink {
	text: string;

	url: string;
	urlRootForSection?: string | undefined;

	disabled: boolean;
	completed: boolean;
	absolute: boolean;

	tooltip?: (navLink: NavLink) => string | undefined;

	children: NavItem[];
}

interface NavSeparator {
	key: string;
	isSeparator: true;
}

export function isSeparator(navItem: NavItem): navItem is NavSeparator {
	return (navItem as NavSeparator)?.isSeparator === true;
}

export function isNavLink(navItem: NavItem): navItem is NavLink {
	return (navItem as NavLink)?.url !== undefined;
}

export function flatMapLinks(link: NavLink): NavLink[] {
	return link.children.length > 0 ? link.children.filter(isNavLink).flatMap(flatMapLinks) : [link];
}
